import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import type { Api } from '../adapters/Api';
import type { Entitlement, UserProfile } from '../domain/types';
import { createApi } from '../adapters/createApi';
import { isBetaActive } from '../domain/beta';
import { hasDownloadableAccess } from '../domain/entitlement';
import { I18nProvider, languages, useI18n } from '../i18n';
import { AuthGate } from './AuthGate';
import { Fleet } from './Fleet';
import { GuidePage } from './GuidePage';
import { Subscribe } from './Subscribe';
import { StatusPill } from './components';
import { SupportWidget } from './SupportWidget';
import { ShieldAlarm } from './ShieldAlarm';
import { ClientConnectionProvider, useClientConnectedValue } from './connection';

// Cáscara con el formato del hosted (apps/web/src/ui/App.tsx): topbar con el
// menú WB (Cuenta · Idioma · Licencia · Configuración · FAQS) + pills de estado.
// La PASARELA sigue igual: con acceso → Dashboard; sin acceso → Subscribe.

const FAQ_URL = 'https://whitebot.app/#faq';
// Página propia dentro del sitio (ver GuidePage.tsx) — pública, sin login.
const GUIDE_URL = '/guia';

function Gated({ api, user, signOut }: { api: Api; user: UserProfile; signOut: () => Promise<void> }) {
  const { t } = useI18n();
  const entitlementQuery = useQuery({ queryKey: ['entitlement'], queryFn: () => api.getEntitlement() });

  // Aviso de altas (doc 14 §6): best-effort en cada sesión; el servidor
  // deduplica y solo la PRIMERA visita del usuario avisa al dueño.
  useEffect(() => {
    void api.notifySignup();
  }, [api]);

  if (entitlementQuery.isLoading) {
    return (
      <main className="auth-shell">
        <div className="panel auth-panel"><p className="muted">{t('entitlement.checking')}</p></div>
      </main>
    );
  }

  const entitlement = entitlementQuery.data ?? { status: 'inactive' as const, plan: null };
  const hasAccess = hasDownloadableAccess(entitlement);

  return (
    <AppShell api={api} user={user} signOut={signOut} entitlement={entitlement} hasAccess={hasAccess}>
      {hasAccess ? (
        <Fleet api={api} />
      ) : (
        <Subscribe api={api} entitlement={entitlement} />
      )}
    </AppShell>
  );
}

function AppShell({
  api,
  user,
  signOut,
  entitlement,
  hasAccess,
  children
}: {
  api: Api;
  user: UserProfile;
  signOut: () => Promise<void>;
  entitlement: Entitlement;
  hasAccess: boolean;
  children: ReactNode;
}) {
  const { t, language, setLanguage } = useI18n();
  const [menuOpen, setMenuOpen] = useState(false);
  const [billingError, setBillingError] = useState('');
  const menuRef = useRef<HTMLDivElement | null>(null);

  // El estado del bot alimenta la pill "cliente conectado" (react-query dedupe
  // con la query del Dashboard: misma clave).
  const statusQuery = useQuery({
    queryKey: ['botStatus'],
    queryFn: () => api.getBotStatus(),
    refetchInterval: 2000,
    enabled: hasAccess
  });

  useEffect(() => {
    if (!menuOpen) return;
    function handlePointerDown(event: PointerEvent) {
      if (!menuRef.current?.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [menuOpen]);

  const billingMutation = useMutation({
    mutationFn: () => api.startBillingPortal(),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: (error) => {
      // Licencia manual (sin cliente de Stripe): aviso informativo, no un error.
      const code = error instanceof Error ? error.message : '';
      setBillingError(code === 'no_stripe_customer' ? t('app.billingNoStripe') : t('app.billingError'));
    }
  });

  const checkoutMutation = useMutation({
    mutationFn: () => api.startCheckout(),
    onSuccess: ({ url }) => {
      window.location.href = url;
    }
  });

  // Fuente única de verdad de "cliente conectado" (p6): este valor alimenta la
  // pill de la cabecera Y el Dashboard (vía contexto) — nunca pueden discrepar.
  const connected = useClientConnectedValue(statusQuery.data);
  const licenseLabel = t(`entitlement.${entitlement.status}` as Parameters<typeof t>[0]);

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="topbar-left">
          <div className="wb-menu-wrap" ref={menuRef}>
            <button
              className="wb-logo"
              aria-label={t('app.openAccountMenu')}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <img src="/logo.png" alt="White Bot" />
            </button>
            {menuOpen && (
              <div className="wb-menu">
                <section>
                  <strong>{t('app.account')}</strong>
                  <span>{user.email}</span>
                  <button type="button" onClick={() => signOut()}>
                    {t('app.signOut')}
                  </button>
                </section>
                <section>
                  <strong>{t('app.language')}</strong>
                  <select value={language} onChange={(event) => setLanguage(event.target.value as typeof language)}>
                    {languages.map((item) => (
                      <option key={item.code} value={item.code}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </section>
                {/* Beta gratuita (dueño 2026-08-02): la sección de Licencia
                    ENTERA se oculta (estado + botones de pago). Reaparece sola
                    cuando la beta termine (isBetaActive false). */}
                {!isBetaActive() && (
                  <section>
                    <strong>{t('app.license')}</strong>
                    <span>{licenseLabel}</span>
                    {hasAccess ? (
                      <button type="button" disabled={billingMutation.isPending} onClick={() => billingMutation.mutate()}>
                        {t('app.manageBilling')}
                      </button>
                    ) : (
                      <button type="button" disabled={checkoutMutation.isPending} onClick={() => checkoutMutation.mutate()}>
                        {t('app.subscribe')}
                      </button>
                    )}
                    {billingError && <span className="license-detail">{billingError}</span>}
                  </section>
                )}
                <section>
                  <strong>{t('app.configuration')}</strong>
                  <span>{t('app.configurationUnavailable')}</span>
                </section>
                <section>
                  <strong>{t('app.guide')}</strong>
                  <a href={GUIDE_URL} target="_blank" rel="noopener" className="wb-menu-link-button">
                    {t('app.guideButton')}
                  </a>
                </section>
                <section>
                  <strong>{t('app.faq')}</strong>
                  <button type="button" onClick={() => { window.location.href = FAQ_URL; }}>
                    {t('app.faqButton')}
                  </button>
                </section>
              </div>
            )}
          </div>
          {hasAccess && (
            <span className="topbar-status" aria-label="Estado del cliente">
              {/* Beta gratuita (dueño 2026-08-02): la pill de LICENCIA se oculta
                  (no mostrar "licencia: prueba"); queda solo la de conexión.
                  Reaparece sola al terminar la beta. */}
              {!isBetaActive() && (
                <StatusPill kind={hasDownloadableAccess(entitlement) ? 'ok' : 'bad'}>
                  {t('pills.license', { label: licenseLabel })}
                </StatusPill>
              )}
              <StatusPill kind={connected ? 'ok' : 'bad'}>
                {connected ? t('pills.connected') : t('pills.disconnected')}
              </StatusPill>
            </span>
          )}
        </div>
        <nav className="topbar-right">
          {/* 0.4 p3: versión del cliente arriba, a la izquierda de Salir */}
          {hasAccess && statusQuery.data?.clientVersion && (
            <span className="topbar-version" title={t('dashboard.clientVersion')}>
              v{statusQuery.data.clientVersion}
            </span>
          )}
          {api.mode === 'supabase' && <button onClick={() => signOut()}>{t('app.signOut')}</button>}
        </nav>
      </header>
      {/* Beta gratuita (domain/beta.ts): visible con y sin acceso; desaparece
          solo al terminar la beta (comprobación en runtime, sin redeploy). */}
      {isBetaActive() && <div className="beta-banner">{t('beta.banner')}</div>}
      <ClientConnectionProvider connected={connected}>{children}</ClientConnectionProvider>
      {/* Aviso "te protegimos de un ataque" (Auto Shield): global para verse en
          la flota Y en el dashboard por-bot; sondea la flota (misma query que
          Fleet, react-query deduplica) y avisa una vez por ataque. */}
      {hasAccess && <ShieldAlarm api={api} />}
      {/* Soporte (doc 14): también SIN acceso — los problemas de pago/licencia
          son justo los que más necesitan el chat. */}
      <SupportWidget api={api} />
    </div>
  );
}

export function App() {
  const api = useMemo(() => createApi(), []);
  // /guia es pública (sin login): quien decide si instalar debe poder leerla antes de crear cuenta.
  if (location.pathname === '/guia') {
    return (
      <I18nProvider>
        <GuidePage />
      </I18nProvider>
    );
  }
  return (
    <I18nProvider>
      <AuthGate api={api}>
        {(user, signOut) => (user ? <Gated api={api} user={user} signOut={signOut} /> : null)}
      </AuthGate>
    </I18nProvider>
  );
}
